/*--------------------------------------------------------------
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * See License.txt in the project root for license information.
 *--------------------------------------------------------------*/

import { buildCacheKey } from '@msdyn365-commerce-modules/retail-actions';
import {
    CacheType,
    createObservableDataAction,
    IAction,
    IActionContext,
    IActionInput,
    ICommerceApiSettings,
    ICreateActionContext
} from '@msdyn365-commerce/core';
import { getContactsAsync } from '../actions/ContactActions/DataActionExtension.g';
import { IContactEntity } from './ContactActions/DataServiceEntities.g';

/**
 * Input class for get customer data action.
 */
export class ContactsInput implements IActionInput {
    private readonly apiSettings: ICommerceApiSettings;

    constructor(apiSettings: ICommerceApiSettings) {
        this.apiSettings = apiSettings;
    }

    public getCacheKey = () => buildCacheKey('Contacts', this.apiSettings);

    public getCacheObjectType = () => 'Contacts';

    public dataCacheType = (): CacheType => 'request';
}

export const createGetContactsInput = (inputData: ICreateActionContext): IActionInput => {
    const { requestContext } = inputData;

    // Check if the user is authenticated.
    if (!requestContext.user.isAuthenticated) {
        throw new Error('User is not logged in.');
    }

    return new ContactsInput(requestContext.apiSettings);
};

export async function getContactUsers(input: ContactsInput, ctx: IActionContext): Promise<IContactEntity[]> {
    return getContactsAsync({ callerContext: ctx })
        .then(users => {
            return users;
        })
        .catch(error => {
            ctx.telemetry.exception(error);
            ctx.trace('[get-customer-contacts] Unable to load users');
            throw new Error('[get-customer-contacts] Unable to load users');
        });
}
export const getContactsDataAction = createObservableDataAction({
    id: 'get-customer-contacts',
    action: <IAction<IContactEntity[]>>(<unknown>getContactUsers),
    input: createGetContactsInput
});

export default getContactsDataAction;
